import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, GripVertical, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import GalleryItemModal from "./GalleryItemModal";

const SUB_TABS = [
  { id: "portfolio", label: "Selected Work" },
  { id: "rooms", label: "In The Room" },
];

export default function GalleryEditor() {
  const [gallery, setGallery] = useState("portfolio");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ portfolio: 0, rooms: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or item object
  const [dragId, setDragId] = useState(null);

  const refresh = async (g = gallery) => {
    setLoading(true);
    try {
      const [active, other] = await Promise.all([
        api.get("/gallery", { params: { gallery: g } }),
        api.get("/gallery", { params: { gallery: g === "portfolio" ? "rooms" : "portfolio" } }),
      ]);
      setItems(active.data);
      setCounts({
        [g]: active.data.length,
        [g === "portfolio" ? "rooms" : "portfolio"]: other.data.length,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh(gallery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery]);

  const openNew = () => setEditing({ gallery });
  const openEdit = (item) => setEditing(item);
  const closeModal = () => setEditing(null);

  const onSaved = async () => {
    setEditing(null);
    await refresh();
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/gallery/${item.id}`);
      toast.success("Item removed.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicateItem = async (item) => {
    try {
      const { id, order, created_at, updated_at, ...rest } = item;
      const payload = { ...rest, title: `Copy of ${item.title}` };
      await api.post("/gallery", payload);
      toast.success("Item duplicated.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Native HTML5 drag-and-drop reorder
  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const from = items.findIndex((x) => x.id === dragId);
    const to = items.findIndex((x) => x.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const reordered = next.map((x, i) => ({ ...x, order: i }));
    setItems(reordered);
    setDragId(null);
    try {
      await api.patch("/gallery/reorder", {
        items: reordered.map((x) => ({ id: x.id, order: x.order })),
      });
    } catch (err) {
      toast.error(formatApiError(err));
      refresh();
    }
  };

  return (
    <div data-testid="gallery-editor">
      {/* Sub-tab switcher */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="inline-flex rounded-sm overflow-hidden border border-white/10">
          {SUB_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setGallery(t.id)}
              className={`px-5 py-2.5 text-sm transition-colors ${gallery === t.id ? "bg-white/8 text-white" : "text-white/55 hover:text-white"}`}
              data-testid={`gallery-subtab-${t.id}`}
            >
              {t.label} ({counts[t.id] || 0})
            </button>
          ))}
        </div>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="gallery-add">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      {/* Item list */}
      <div className="mt-6">
        {loading ? (
          <div className="glass rounded-sm p-10 grid place-items-center" data-testid="gallery-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--pov-cyan)]" />
          </div>
        ) : items.length === 0 ? (
          <div className="glass rounded-sm p-10 text-center text-white/55" data-testid="gallery-empty">
            <p className="text-sm">No items yet in {gallery === "rooms" ? "In The Room" : "Selected Work"}.</p>
            <button onClick={openNew} className="btn-primary text-sm mt-4" data-testid="gallery-empty-add">
              <Plus className="w-4 h-4" /> Add the first item
            </button>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="gallery-list">
            {items.map((item) => (
              <li
                key={item.id}
                draggable
                onDragStart={(e) => onDragStart(e, item.id)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, item.id)}
                className={`group flex items-center gap-4 p-3 rounded-sm glass border border-white/5 hover:border-white/20 transition-all ${dragId === item.id ? "opacity-50" : ""}`}
                data-testid={`gallery-row-${item.id}`}
              >
                <button
                  type="button"
                  className="cursor-grab text-white/30 hover:text-white/70 active:cursor-grabbing"
                  aria-label="Drag to reorder"
                  data-testid={`gallery-drag-${item.id}`}
                >
                  <GripVertical className="w-5 h-5" />
                </button>
                <div className="w-20 h-14 rounded-sm overflow-hidden flex-shrink-0 bg-[var(--pov-bg-3)]">
                  {item.image_data ? (
                    <img src={item.image_data} alt={item.title} className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.field ? (
                      <span className="text-[10px] tracking-wider uppercase font-bold text-[var(--pov-coral)]">{item.field}</span>
                    ) : null}
                    {item.section ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-sm bg-white/5 text-white/60">{item.section}</span>
                    ) : null}
                  </div>
                  <p className="font-display text-base mt-0.5 truncate">{item.title}</p>
                  {item.location ? <p className="text-xs text-white/45 truncate">{item.location}</p> : null}
                </div>
                <div className="flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 hover:border-[var(--pov-cyan)] text-white/70 hover:text-[var(--pov-cyan)]"
                    aria-label="Edit"
                    data-testid={`gallery-edit-${item.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => duplicateItem(item)}
                    className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 hover:border-white/40 text-white/70 hover:text-white"
                    aria-label="Duplicate"
                    data-testid={`gallery-duplicate-${item.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(item)}
                    className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 hover:border-[var(--pov-coral)] text-white/70 hover:text-[var(--pov-coral)]"
                    aria-label="Delete"
                    data-testid={`gallery-delete-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-4 text-xs text-white/35">
          Drag rows by the handle to reorder. Click the pencil to edit text & image. Sections group items under headings on the live site.
        </p>
      </div>

      {editing ? (
        <GalleryItemModal
          gallery={editing.gallery || gallery}
          item={editing}
          onClose={closeModal}
          onSaved={onSaved}
        />
      ) : null}
    </div>
  );
}
